import { useEffect, useRef, useState } from 'react';
import CommandPalette from './components/CommandPalette';
import DiffView from './components/DiffView';
import DocumentTabs from './components/DocumentTabs';
import Editor from './components/Editor';
import PaneTabs, { type Pane } from './components/PaneTabs';
import Preview from './components/Preview';
import ShortcutCheatsheet from './components/ShortcutCheatsheet';
import Splitter, { MAX_RATIO, MIN_RATIO } from './components/Splitter';
import StatusBar from './components/StatusBar';
import Topbar from './components/Topbar';
import { DIAGRAM_TYPES, detectDiagramType, extractOpeningToken, type DiagramType } from './lib/diagramTypes';
import { copyPngToClipboard, saveSvg } from './lib/export';
import { deriveRowTitle, generateId, HISTORY_STORAGE_KEY, shelveBuffer, shouldShelve, type HistoryRow } from './lib/history';
import { extractMermaidSource, isMarkdownFilename } from './lib/importSource';
import { DRAFT_STORAGE_KEY, getInitialSource, type DraftBuffer } from './lib/initialSource';
import { isDiagramTheme, isLook, renderMermaid, setMermaidConfig, type DiagramTheme, type Look } from './lib/mermaidRender';
import { MERMAID_VERSION } from './lib/mermaidVersion';
import { createRenderLoop, type LoopState } from './lib/render';
import { encodeShareState } from './lib/share';
import { isEditableTarget } from './lib/shortcuts';
import { readStorage, removeStorage, writeStorage } from './lib/storage';
import { detectUnclosedQuote } from './lib/syntaxHints';
import {
  readBackgroundTabs,
  removeBackgroundTab,
  TABS_STORAGE_KEY,
  upsertBackgroundTab,
  writeBackgroundTabs,
  type BackgroundTab,
} from './lib/tabs';

const INITIAL_LOOP_STATE: LoopState = { state: 'empty', svg: null, error: null, renderMs: null, notice: null };
// How long an export/copy confirmation ("Saved SVG.") stays in the status
// bar's notice slot before clearing itself — unlike the render loop's own
// `notice` (M4.5), which clears on a definite event (attempt start/end),
// an action confirmation has no such event to key off, so it's timer-based.
const ACTION_NOTICE_MS = 2500;

// The registry (M4.1, issue #15) replaces M1's hardcoded default source
// constant — SPEC.md §12 M1's own note: "M4 swaps it for a registry lookup".
function getDefaultDiagramType(): DiagramType {
  const type = DIAGRAM_TYPES.find((t) => t.id === 'flowchart');
  if (!type) throw new Error('diagramTypes registry must include the flowchart entry');
  return type;
}
const DEFAULT_DIAGRAM_TYPE = getDefaultDiagramType();
const SPLITTER_STORAGE_KEY = 'plotter:splitter-ratio';
const DEFAULT_RATIO = 0.5;
const SPLITTER_WRITE_DEBOUNCE_MS = 300;
const DEFAULT_DIAGRAM_THEME: DiagramTheme = 'default';
const DEFAULT_LOOK: Look = 'classic';
// SPEC.md §11: "Below 820px: panes become tabs." A `max-width` media query,
// not a `ResizeObserver` on the shell itself — this is a viewport-level
// layout switch (Editor/Preview both stay mounted throughout; only which
// one is visible changes), not something tied to any one element's size.
const NARROW_MEDIA_QUERY = '(max-width: 819px)';
// Shared by the hash-sync and plotter:draft autosave effects (M6.2/M6.3) —
// both are debounced reactions to the same `source` change, so one timer
// serves both rather than two near-identical effects differing only in
// what they persist.
const SOURCE_PERSIST_DEBOUNCE_MS = 600;

function readPersistedRatio(): number {
  const raw = readStorage(SPLITTER_STORAGE_KEY);
  const parsed = raw === null ? NaN : Number(raw);
  if (!Number.isFinite(parsed) || parsed < MIN_RATIO || parsed > MAX_RATIO) return DEFAULT_RATIO;
  return parsed;
}

// The one-time hash → draft → template decision (SPEC.md §7), plus the
// buffer identity (`id`/`originalCode`) history.ts's `shouldShelve` needs to
// tell an edited buffer from an untouched one. Computed exactly once per
// mount via the lazy-ref pattern below — `getInitialSource` mints a fresh
// id via `crypto.randomUUID()` when there's no draft to inherit one from,
// so calling it twice per mount (as M6.2/M6.3 did, to dodge a stale-closure
// `[]`-effect dependency) would hand the same buffer two different ids.
interface Bootstrap {
  code: string;
  id: string;
  originalCode: string;
  outgoingDraft: DraftBuffer | null;
  diagramTheme: DiagramTheme;
  look: Look;
}

// A hash's diagramTheme/look are decoded as plain strings (share.ts's
// ShareState doesn't narrow them — a hash is foreign input, decoded and
// validated the same way anywhere else in the app treats it). A value
// outside this app's own 5-theme/2-look catalog (a future version's value,
// or a hand-edited hash) falls back to the default rather than being
// handed to Mermaid unvalidated.
function bootstrap(fallback: string): Bootstrap {
  const decision = getInitialSource(fallback);
  if (decision.from === 'draft') {
    // plotter:draft's own schema (initialSource.ts's DraftBuffer) doesn't
    // carry diagramTheme/look — only the hash does (SPEC.md §7's AC for
    // this ticket is scoped to "a shared link reproduces" them, not a
    // restored draft) — so a draft-restored session always starts at the
    // defaults, same as a fresh template.
    return {
      code: decision.code,
      id: decision.id,
      originalCode: decision.originalCode,
      outgoingDraft: null,
      diagramTheme: DEFAULT_DIAGRAM_THEME,
      look: DEFAULT_LOOK,
    };
  }
  if (decision.from === 'hash') {
    return {
      code: decision.code,
      id: generateId(),
      originalCode: decision.code,
      outgoingDraft: decision.outgoingDraft,
      diagramTheme: isDiagramTheme(decision.diagramTheme) ? decision.diagramTheme : DEFAULT_DIAGRAM_THEME,
      look: isLook(decision.look) ? decision.look : DEFAULT_LOOK,
    };
  }
  return {
    code: decision.code,
    id: generateId(),
    originalCode: decision.code,
    outgoingDraft: null,
    diagramTheme: DEFAULT_DIAGRAM_THEME,
    look: DEFAULT_LOOK,
  };
}

/**
 * SPEC.md §3 — the shell: 52px topbar, two panes, 30px status bar.
 *
 * M1 wires the render loop: a plain `<textarea>` in Source, the Mermaid
 * adapter feeding `createRenderLoop`, and Preview/StatusBar driven by its
 * emitted state. M2.6 (issue #5) adds the draggable, persisted splitter; the
 * sheet/dot-grid framing and pan/zoom/fit live inside Preview itself.
 */
export default function App() {
  // Lazy-init guard, not a `useState`/`useMemo` — the value must survive
  // StrictMode's double-render of the function body untouched (a second
  // `bootstrap()` call would mint a second, different id), and unlike
  // `useMemo` a ref is never dropped and recomputed under memory pressure.
  const bootstrapRef = useRef<Bootstrap | null>(null);
  if (bootstrapRef.current === null) {
    bootstrapRef.current = bootstrap(DEFAULT_DIAGRAM_TYPE.template);
  }
  const initialBuffer = bootstrapRef.current;

  const [source, setSource] = useState(initialBuffer.code);
  const [diagramTheme, setDiagramTheme] = useState<DiagramTheme>(initialBuffer.diagramTheme);
  const [look, setLook] = useState<Look>(initialBuffer.look);
  const [loopState, setLoopState] = useState<LoopState>(INITIAL_LOOP_STATE);
  const [ratio, setRatio] = useState(readPersistedRatio);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [diffViewOpen, setDiffViewOpen] = useState(false);
  // Multi-diagram tabs (SPEC.md §13, tabs.ts's own doc comment): every OTHER
  // open buffer, backgrounded — the currently active one stays exactly
  // `source`/`sourceIdRef`/`originalSourceRef` above, untouched by this.
  // Lazy-init from `plotter:tabs`, same pattern as `readPersistedRatio`.
  const [backgroundTabs, setBackgroundTabs] = useState<BackgroundTab[]>(readBackgroundTabs);
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_MEDIA_QUERY).matches);
  const [activeTab, setActiveTab] = useState<Pane>('source');
  const loopRef = useRef<ReturnType<typeof createRenderLoop> | null>(null);
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ratioRef = useRef(ratio);
  const actionNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Buffer identity (SPEC.md §7's shelve invariant) — plain refs, not state,
  // since they're only ever read imperatively at the moment a buffer is
  // wholesale-replaced, never rendered.
  const sourceIdRef = useRef(initialBuffer.id);
  const originalSourceRef = useRef(initialBuffer.originalCode);
  // The one piece of buffer identity that *is* state, not a ref: Preview.tsx
  // needs to react to "the buffer itself changed" (New, Recent, drag-drop,
  // hash load) to re-arm auto-fit for that diagram's first render (SPEC.md
  // §3), as distinct from "the same buffer's content changed" (plain
  // typing), which must never re-fit and disturb the user's own pan/zoom.
  // sourceIdRef above can't serve this — a ref update doesn't trigger a
  // re-render, so Preview would never see it change.
  const [bufferId, setBufferId] = useState(initialBuffer.id);
  // The pending timer from the source-persist effect below — exposed as a
  // ref (not just the effect's own local closure) so handleClearAllData can
  // cancel it. Without this, a Clear that lands inside the initial
  // SOURCE_PERSIST_DEBOUNCE_MS window after mount gets silently undone: the
  // already-scheduled write fires moments later and rewrites plotter:draft
  // with whatever's currently in the buffer.
  const sourcePersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref-to-latest-callback (same pattern as useEscapeKey.ts) so the `[]`
  // drag-drop effect below can call the current `replaceBuffer` — which
  // closes over `source` and is redefined every render — without listing
  // it as a dependency and re-registering the window listeners on every
  // keystroke.
  const replaceBufferRef = useRef<(newCode: string, newId: string) => void>(() => {});

  const lineCount = source.length === 0 ? 0 : source.split('\n').length;

  // Recomputed on every render — a cheap, single pass over `source`'s lines
  // (SPEC.md §4's "Type detection"), not worth a separate state slice.
  const diagramType = detectDiagramType(source);
  // SPEC.md §4: "An unmatched first line reads `Unrecognised` in the status
  // bar" — distinct from StatusBar's own null-segment em dash, which is for
  // "nothing typed yet" (an empty source has no line to fail to match).
  const diagramTypeLabel = source.trim() === '' ? null : (diagramType?.label ?? 'Unrecognised');
  // True only when detection matched via an *alias* rather than the entry's
  // canonical keyword — e.g. the user wrote `sankey` but the registry's
  // canonical keyword is `sankey-beta` (SPEC.md §4's "Beta keywords
  // change"). Sourced entirely from the registry, never parsed out of
  // Mermaid's own error text.
  const matchedViaAlias = diagramType !== null && extractOpeningToken(source) !== diagramType.keyword;
  // A structural fact about the source, computed independently of Mermaid's
  // own message — only worth the (trivial) recompute while an error card is
  // actually showing (syntaxHints.ts's own doc comment explains why this
  // one check is safe to show unprompted, unlike a paraphrase of Mermaid's
  // parser would be).
  const syntaxHint = loopState.error !== null ? detectUnclosedQuote(source) : null;

  useEffect(() => {
    const loop = createRenderLoop({ render: renderMermaid });
    loopRef.current = loop;
    const unsubscribe = loop.subscribe(setLoopState);
    // Reads the SAME cached bootstrap decision `source`'s own `useState`
    // initializer used (via the ref, not a recompute) — so a shared link's
    // or restored draft's diagram never flashes to the default template
    // first, and `bootstrap()` still only ever runs once per mount.
    const boot = bootstrapRef.current!;
    // A non-default theme/look (only ever from a hash — see bootstrap())
    // must be applied to Mermaid *before* the first setSource, or the
    // opening render draws at the default and then immediately redraws —
    // a visible flash a shared link should never cause. A default/default
    // boot is a no-op inside setMermaidConfig itself, so this costs nothing
    // extra on the far more common path.
    void setMermaidConfig(boot.diagramTheme, boot.look).then(() => {
      loop.setSource(boot.code);
    });

    // SPEC.md §7's shelve invariant, applied to the one non-interactive
    // trigger: the URL hash overriding an existing plotter:draft at load.
    // The other three triggers (New, drag-drop, Recent) go through
    // replaceBuffer below instead. Safe under StrictMode's dev-mode
    // double-invoke of this effect: upsertHistoryRow upserts by id, so
    // shelving the same outgoing draft twice in a row just rewrites the
    // same row rather than creating a twin.
    if (boot.outgoingDraft !== null && shouldShelve(boot.outgoingDraft)) {
      shelveBuffer(boot.outgoingDraft.id, boot.outgoingDraft.code);
    }

    return () => {
      unsubscribe();
      loop.destroy();
      loopRef.current = null;
    };
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        loopRef.current?.forceRender();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // SPEC.md §11, issue #37 (M6.10). matchMedia's own change event, not a
  // window `resize` listener — it only fires when the query's match state
  // actually flips (crossing the 820px boundary), not on every pixel of a
  // drag-resize, so this never re-renders more than the layout genuinely
  // needs to change.
  useEffect(() => {
    const mediaQuery = window.matchMedia(NARROW_MEDIA_QUERY);
    function onChange() {
      setIsNarrow(mediaQuery.matches);
    }
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  // Cmd/Ctrl+B — SPEC.md §11, issue #37. "active only below the 820px
  // breakpoint": above it, this returns before ever calling
  // preventDefault(), so a browser/OS binding on the same combo (Chrome's
  // bookmark-bar toggle, for one) is left alone — there's no tab strip to
  // switch between when both panes are already visible.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!isNarrow) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setActiveTab((tab) => (tab === 'source' ? 'preview' : 'source'));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isNarrow]);

  // Cmd/Ctrl+K — SPEC.md §10, issue #35. Unconditional, no isEditableTarget
  // check: unlike the bare `?` cheatsheet shortcut below, this carries a
  // required modifier key, so it can never collide with a character the
  // user is legitimately typing — same reasoning as Cmd/Ctrl+Enter above.
  // preventDefault stops the browser's own "focus the address bar" binding
  // on this same combo.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // A confirmation ("Saved SVG.") for export/copy actions — SPEC.md §9's
  // copy rule (name the action, same verb in the confirmation). Shown in
  // StatusBar's notice slot (the same one M4.5's ZenUML-loading notice
  // uses), auto-clearing after ACTION_NOTICE_MS since — unlike that
  // notice — there's no definite "done" event to key off.
  function showActionNotice(message: string) {
    setActionNotice(message);
    if (actionNoticeTimerRef.current !== null) clearTimeout(actionNoticeTimerRef.current);
    actionNoticeTimerRef.current = setTimeout(() => {
      actionNoticeTimerRef.current = null;
      setActionNotice(null);
    }, ACTION_NOTICE_MS);
  }

  useEffect(() => {
    return () => {
      if (actionNoticeTimerRef.current !== null) clearTimeout(actionNoticeTimerRef.current);
    };
  }, []);

  // Cmd/Ctrl+Shift+S — SPEC.md §10. Fixed at the non-transparent default
  // regardless of the Export menu's own toggle, matching Cmd/Ctrl+Shift+C
  // below: a global shortcut with no menu open has nothing to read a
  // hidden toggle from, so it stays predictable rather than silently
  // depending on menu state the user can't see.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (loopState.svg === null) return;
        saveSvg(loopState.svg, `${diagramType?.id ?? 'diagram'}.svg`, false);
        showActionNotice('Saved SVG.');
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loopState.svg, diagramType]);

  // Cmd/Ctrl+Shift+C — SPEC.md §6: "the single most-used action." Always
  // PNG at 1x (see copyPngToClipboard's own doc comment for why).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        if (loopState.svg === null) return;
        void copyPngToClipboard(loopState.svg)
          .then(() => showActionNotice('Copied PNG to clipboard.'))
          .catch((err: unknown) => console.error('Copy PNG to clipboard failed:', err));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loopState.svg]);

  // Drag-and-drop import — SPEC.md §6. Window-level, not on any one pane,
  // so it works "regardless of which pane has focus" (issue #26).
  // `dragover` must call preventDefault() or the browser rejects the drop
  // outright and navigates to open the file instead. A successful drop is a
  // wholesale buffer replacement, same as the New menu — goes through
  // replaceBuffer so the outgoing buffer gets shelved first if it was edited.
  //
  // Capture phase: CodeMirror's own drop handling (dropCursor) is
  // currently a passive observer that never stops propagation, so this
  // isn't fixing an observed bug — it's cheap insurance against a future
  // CodeMirror version, or any other nested handler, doing so.
  useEffect(() => {
    function onDragOver(e: DragEvent) {
      e.preventDefault();
    }

    function onDrop(e: DragEvent) {
      e.preventDefault();
      const file = e.dataTransfer?.files[0];
      if (!file) return;
      void file.text().then((content) => {
        const extracted = extractMermaidSource(file.name, content);
        if (extracted !== null) {
          replaceBufferRef.current(extracted, generateId());
          return;
        }
        showActionNotice(
          isMarkdownFilename(file.name)
            ? 'No Mermaid code block found in that file.'
            : "Can't import that file — drop a .mmd, .mermaid, .txt, or .md file.",
        );
      });
    }

    window.addEventListener('dragover', onDragOver, { capture: true });
    window.addEventListener('drop', onDrop, { capture: true });
    return () => {
      window.removeEventListener('dragover', onDragOver, { capture: true });
      window.removeEventListener('drop', onDrop, { capture: true });
    };
  }, []);

  // URL hash sharing (SPEC.md §7) + plotter:draft autosave (SPEC.md §7,
  // issue #30) — one debounced effect for both, since they're triggered by
  // the exact same `source` change on the exact same cadence; two
  // near-identical effects differing only in what they persist would just
  // be Duplicated Code. Never on every keystroke — replaceState, not
  // pushState, and always reflects the *current* buffer once the debounce
  // settles, so the address bar never freezes on a stale payload while
  // further edits happen. Effect-based debounce: React's own cleanup
  // cancels the pending timer whenever `source` changes again before it
  // fires, no ref needed. When the encoded hash payload would exceed the
  // size limit, the hash clears entirely rather than carry a truncated,
  // undecodable fragment — plotter:draft has no such limit, so it always
  // saves regardless.
  //
  // M6.6 (issue #33): also keyed on diagramTheme/look now, not just source —
  // "people share by copying the address bar" (this file's own comment,
  // above), so a theme-only change (no source edit) must still eventually
  // land in the address bar, not just in ShareMenu's own live-read props.
  // plotter:draft's write is unaffected (its schema doesn't carry theme/look
  // — see bootstrap()'s draft branch) but restarting on the same cadence is
  // harmless: it just re-persists the same code a little sooner.
  useEffect(() => {
    const timer = setTimeout(() => {
      sourcePersistTimerRef.current = null;
      writeStorage(
        DRAFT_STORAGE_KEY,
        JSON.stringify({ id: sourceIdRef.current, code: source, originalCode: originalSourceRef.current }),
      );

      const encoded = encodeShareState({
        code: source,
        diagramTheme,
        look,
        version: 1,
      });
      const path = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', encoded === null ? path : `${path}#${encoded}`);
    }, SOURCE_PERSIST_DEBOUNCE_MS);
    sourcePersistTimerRef.current = timer;
    return () => clearTimeout(timer);
  }, [source, diagramTheme, look]);

  // `?` — SPEC.md §10. No modifier, so it's also a character a user
  // legitimately wants to type into Mermaid source (a decision node's
  // label, e.g. `{Known signature?}`) — isEditableTarget() gates this to
  // only fire when focus isn't somewhere accepting text input.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== '?' || isEditableTarget(e.target)) return;
      e.preventDefault();
      setCheatsheetOpen(true);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Plain typing — never touches buffer identity (sourceIdRef/
  // originalSourceRef stay put), so an edited draft keeps accumulating
  // against its original baseline rather than resetting on every keystroke.
  function handleSourceChange(next: string) {
    setSource(next);
    loopRef.current?.setSource(next);
  }

  // The one shelve-then-replace path every wholesale buffer swap uses (New,
  // drag-drop import, Recent) — SPEC.md §7's shelve invariant: an edited
  // outgoing buffer is saved to plotter:history before it's overwritten.
  // `handleSourceChange` above is deliberately NOT built on this — ordinary
  // typing must never mint a new id or move the shelve baseline.
  function replaceBuffer(newCode: string, newId: string) {
    if (shouldShelve({ code: source, originalCode: originalSourceRef.current })) {
      shelveBuffer(sourceIdRef.current, source);
    }
    sourceIdRef.current = newId;
    originalSourceRef.current = newCode;
    setSource(newCode);
    setBufferId(newId);
    loopRef.current?.setSource(newCode);
  }
  replaceBufferRef.current = replaceBuffer;

  function handleSelectTemplate(type: DiagramType) {
    replaceBuffer(type.template, generateId());
  }

  function handleRestoreFromHistory(row: HistoryRow) {
    replaceBuffer(row.code, row.id);
  }

  function persistBackgroundTabs(next: BackgroundTab[]) {
    setBackgroundTabs(next);
    writeBackgroundTabs(next);
  }

  // The active buffer's *current* code, as a BackgroundTab — used whenever
  // it's about to be parked in the strip (opening a new tab, switching to
  // another one). Titled fresh from its live content, same derivation
  // history.ts's own shelveBuffer uses, so a tab's label in the strip always
  // reflects what's actually in it right now, not a stale template name.
  function currentAsBackgroundTab(): BackgroundTab {
    return { id: sourceIdRef.current, title: deriveRowTitle(source, diagramType), code: source };
  }

  // "+" on the tab strip — SPEC.md §13's tabs stretch goal. Deliberately NOT
  // built on replaceBuffer/shouldShelve: nothing is being destroyed here,
  // the outgoing buffer is parked in `plotter:tabs` instead of shelved to
  // history (or discarded), so there's no "was it edited" check to make —
  // it's always still reachable, unconditionally, from the strip itself.
  function handleOpenNewTab() {
    persistBackgroundTabs(upsertBackgroundTab(backgroundTabs, currentAsBackgroundTab()));
    const newId = generateId();
    sourceIdRef.current = newId;
    originalSourceRef.current = DEFAULT_DIAGRAM_TYPE.template;
    setSource(DEFAULT_DIAGRAM_TYPE.template);
    setBufferId(newId);
    loopRef.current?.setSource(DEFAULT_DIAGRAM_TYPE.template);
  }

  // Clicking a background tab — parks the outgoing active buffer the same
  // way handleOpenNewTab does, then swaps the clicked tab in via
  // replaceBuffer, exactly the mechanism restoring from Recent already uses
  // (same "treat the incoming code as its own fresh baseline" semantics).
  function handleSelectTab(tab: BackgroundTab) {
    persistBackgroundTabs(upsertBackgroundTab(removeBackgroundTab(backgroundTabs, tab.id), currentAsBackgroundTab()));
    replaceBuffer(tab.code, tab.id);
  }

  // Closing a background tab — shelved to plotter:history unconditionally
  // (SPEC.md §7's Recent list) since a backgrounded tab's own "edited since
  // when" baseline isn't tracked once it's parked; unlike shouldShelve's
  // active-buffer check, always shelving here means a closed tab is always
  // recoverable, the same guarantee closing a browser tab gives you.
  function handleCloseTab(tab: BackgroundTab) {
    shelveBuffer(tab.id, tab.code);
    persistBackgroundTabs(removeBackgroundTab(backgroundTabs, tab.id));
  }

  // Closing the ACTIVE tab — same unconditional shelve as closing a
  // background one, then activates the most-recently-backgrounded tab if
  // any exist, else starts fresh at the default template (never zero tabs
  // on screen, the same invariant a first load already guarantees).
  function handleCloseActiveTab() {
    shelveBuffer(sourceIdRef.current, source);
    const [next, ...rest] = backgroundTabs;
    persistBackgroundTabs(rest);
    if (next) {
      replaceBuffer(next.code, next.id);
    } else {
      replaceBuffer(DEFAULT_DIAGRAM_TYPE.template, generateId());
    }
  }

  // SPEC.md §8: diagram theme and look belong to the diagram, so selecting
  // either re-initializes Mermaid (mermaidRender.ts's setMermaidConfig,
  // no-op if unchanged) and then forces the currently-displayed diagram to
  // redraw under the new config — Mermaid's init options don't retroactively
  // touch anything already on the sheet.
  function handleDiagramThemeChange(next: DiagramTheme) {
    setDiagramTheme(next);
    void setMermaidConfig(next, look).then(() => {
      loopRef.current?.forceRender();
    });
  }

  function handleLookChange(next: Look) {
    setLook(next);
    void setMermaidConfig(diagramTheme, next).then(() => {
      loopRef.current?.forceRender();
    });
  }

  // SPEC.md §7: "Add a 'Clear all local data' item in the About menu."
  // Wipes both keys §7 defines; the confirmation step itself lives in
  // AboutMenu, not here. Deliberately doesn't touch the running buffer —
  // the AC only promises a fresh-load experience "on reload" (precedence
  // then falls to the flowchart template with no draft to inherit), so
  // there's nothing to reset in the current session beyond storage.
  //
  // Cancels any pending source-persist write first — otherwise a Clear
  // inside that debounce's window gets silently undone moments later when
  // the already-scheduled write fires and rewrites plotter:draft.
  function handleClearAllData() {
    if (sourcePersistTimerRef.current !== null) {
      clearTimeout(sourcePersistTimerRef.current);
      sourcePersistTimerRef.current = null;
    }
    removeStorage(DRAFT_STORAGE_KEY);
    removeStorage(HISTORY_STORAGE_KEY);
    removeStorage(TABS_STORAGE_KEY);
    // The AC's "nothing to reset in the current session beyond storage"
    // reasoning above doesn't quite hold for background tabs the way it
    // does for the running buffer: unlike `source`, `backgroundTabs` is
    // rendered as the tab strip itself, so leaving the in-memory list as-is
    // would show tabs a reload can no longer restore (their `plotter:tabs`
    // backing is gone) — visibly wrong, not just eventually stale.
    setBackgroundTabs([]);
    showActionNotice('Cleared all local data.');
  }

  // The ratio itself is instant (every pointermove updates the layout);
  // only the localStorage write is debounced, so a fast drag doesn't fire a
  // synchronous write on every pixel of movement.
  function handleRatioChange(next: number) {
    setRatio(next);
    ratioRef.current = next;
    if (writeTimerRef.current !== null) clearTimeout(writeTimerRef.current);
    writeTimerRef.current = setTimeout(() => {
      writeTimerRef.current = null;
      writeStorage(SPLITTER_STORAGE_KEY, String(next));
    }, SPLITTER_WRITE_DEBOUNCE_MS);
  }

  // Flush a pending debounced write immediately on unload/reload, so a drag
  // followed right away by Cmd/Ctrl+R doesn't lose the change.
  useEffect(() => {
    function flush() {
      if (writeTimerRef.current === null) return;
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
      writeStorage(SPLITTER_STORAGE_KEY, String(ratioRef.current));
    }
    window.addEventListener('beforeunload', flush);
    return () => {
      flush();
      window.removeEventListener('beforeunload', flush);
    };
  }, []);

  return (
    <div className="app-shell-height grid grid-rows-[minmax(52px,auto)_auto_1fr_30px] overflow-hidden">
      <Topbar
        onSelectTemplate={handleSelectTemplate}
        onRestoreFromHistory={handleRestoreFromHistory}
        source={source}
        svg={loopState.svg}
        diagramType={diagramType}
        diagramTheme={diagramTheme}
        look={look}
        onDiagramThemeChange={handleDiagramThemeChange}
        onLookChange={handleLookChange}
        onExport={showActionNotice}
        onCopy={showActionNotice}
        onClearAllData={handleClearAllData}
        onOpenDiff={() => setDiffViewOpen(true)}
        isNarrow={isNarrow}
      />
      <DocumentTabs
        activeTitle={deriveRowTitle(source, diagramType)}
        tabs={backgroundTabs}
        onSelect={handleSelectTab}
        onCloseActive={handleCloseActiveTab}
        onClose={handleCloseTab}
        onNew={handleOpenNewTab}
      />
      {/*
        SPEC.md §11, issue #37: below 820px this becomes a single flex
        column (tab strip + one visible pane) instead of the three-column
        grid. Editor and Preview stay mounted in the exact same JSX
        positions either way — `key`s pin their wrapper `div`s so React
        never unmounts/remounts them (and so CodeMirror's EditorView /
        Preview's pan-zoom state) just because a sibling (the tab strip,
        the splitter) appears or disappears around them. Each wrapper is
        `contents` when its pane should render normally (both, above the
        breakpoint; whichever `activeTab` is, below it) and `hidden`
        otherwise — `display: none` on the wrapper removes it from grid
        track sizing entirely, so the hidden pane's grid column collapses
        away rather than leaving blank space (there's nothing to render
        differently above 820px: both wrappers are always `contents`
        there, so this whole mechanism is inert and behavior stays
        pixel-identical to before this ticket, per its own AC).
      */}
      <main
        className={isNarrow ? 'flex min-h-0 flex-col' : 'grid min-h-0'}
        style={isNarrow ? undefined : { gridTemplateColumns: `${ratio}fr auto ${1 - ratio}fr` }}
      >
        {isNarrow && <PaneTabs activeTab={activeTab} onSelect={setActiveTab} />}
        <div key="editor-wrapper" className={!isNarrow || activeTab === 'source' ? 'contents' : 'hidden'}>
          <Editor value={source} onChange={handleSourceChange} error={loopState.error} />
        </div>
        {!isNarrow && <Splitter ratio={ratio} onChange={handleRatioChange} />}
        <div key="preview-wrapper" className={!isNarrow || activeTab === 'preview' ? 'contents' : 'hidden'}>
          <Preview
            state={loopState.state}
            svg={loopState.svg}
            error={loopState.error}
            diagramType={diagramType}
            matchedViaAlias={matchedViaAlias}
            syntaxHint={syntaxHint}
            bufferId={bufferId}
          />
        </div>
      </main>
      <StatusBar
        state={loopState.state}
        diagramType={diagramTypeLabel}
        lineCount={lineCount}
        renderMs={loopState.renderMs}
        mermaidVersion={MERMAID_VERSION}
        // loopState.notice wins if both are ever set at once — a plugin
        // actively loading is a more load-bearing thing to surface than an
        // already-completed export's confirmation, and actionNotice's own
        // ACTION_NOTICE_MS timeout means it's never stuck waiting long.
        notice={loopState.notice ?? actionNotice}
      />
      {cheatsheetOpen && <ShortcutCheatsheet onClose={() => setCheatsheetOpen(false)} />}
      {diffViewOpen && (
        <DiffView currentSource={source} currentSvg={loopState.svg} onClose={() => setDiffViewOpen(false)} />
      )}
      {commandPaletteOpen && (
        <CommandPalette
          source={source}
          svg={loopState.svg}
          diagramType={diagramType}
          onSelectTemplate={handleSelectTemplate}
          onExport={showActionNotice}
          onCopy={showActionNotice}
          onDiagramThemeChange={handleDiagramThemeChange}
          onLookChange={handleLookChange}
          onClearAllData={handleClearAllData}
          onClose={() => setCommandPaletteOpen(false)}
        />
      )}
    </div>
  );
}
